import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import RoundCard from 'ext-ui/components/RoundCard/RoundCard'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import MagicalAIIcon from 'ext-ui/svg/icon/MagicalAIIcon'
import Markdown from 'markdown-to-jsx'
import React, { ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import ListModalCorner from 'src/assets/images/ListModalCorner.png'
import { useTranslate } from 'src/localization/Trns'
import { peopleMembersUrl, upgradeSubscriptionUrl } from 'src/navigation/urls'
import { useQueryOrganizationUsages } from 'src/queries/organizations/saas.queries'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'

import style from './DowngradedToStarterModal.style'

type DowngradedToStarterModalProps = Omit<BaseModalProps, 'variant'>

export default function DowngradedToStarterModal(
  props: DowngradedToStarterModalProps,
): ReactElement {
  const { className = '', isOpen, onClose } = props
  const translate = useTranslate()
  const history = useHistory()

  const org = useSelector(selectUserExplicitOrganizationListModelFromState)
  const { data: orgUsages } = useQueryOrganizationUsages({
    orgId: org?.id,
  })

  const overUserLimit =
    !orgUsages?.features.ORGANIZATION_TOTAL_SIZE?.withinLimit ||
    !orgUsages?.features.GUEST_ROLE?.withinLimit

  return (
    <BaseModal
      className={cn('DowngradedToStarterModal', className)}
      variant="interactive"
      isOpen={isOpen}
      onClose={onClose}
      css={style}
      aria-label={translate('Downgraded to Starter Plan Modal')}
    >
      <div className="DowngradedToStarterModal-body flex-column gap-5">
        <img
          className="DowngradedToStarterModal-Corner-image"
          src={ListModalCorner}
          alt="List Illustration"
        />
        <p className="font-h2">{translate('You are now on a Starter plan!')}</p>
        <p className="font-regular">
          {translate(
            'Please take the actions below to unlock all of your Starter features',
          )}
        </p>
        <RoundCard className="gap-6">
          <p className="font-h3">
            {translate('Features you can unlock by upgrading to the Pro plan:')}
          </p>
          {/* LIST OF PRO FEATURES*/}
          <div className="Features-list-container flex-column gap-2">
            <div className="Features-list-item flex-row gap-1 justify-start align-center">
              <div className="Feature-list-item-icon">
                <CheckmarkIcon />
              </div>
              <div className="Feature-list-item-text">
                {translate('5+ users in your organization')}
              </div>
            </div>
            <div className="Features-list-item flex-row gap-1 justify-start align-center">
              <div className="Feature-list-item-icon">
                <CheckmarkIcon />
              </div>
              <div className="Feature-list-item-text">
                {translate('Up to 500 virtual cards per month*')}
              </div>
            </div>
            <div className="Features-list-item flex-row gap-1 justify-start align-center">
              <div className="Feature-list-item-icon">
                <MagicalAIIcon />
              </div>
              <div className="Feature-list-item-text">
                {translate('Advanced receipt management')}
              </div>
            </div>
            <div className="Features-list-item flex-row gap-1 justify-start align-center">
              <div className="Feature-list-item-icon">
                <MagicalAIIcon />
              </div>
              <div className="Feature-list-item-text">
                {translate(
                  'Additional expense categories, labels, and automatic coding',
                )}
              </div>
            </div>
            <div className="Features-list-item flex-row gap-1 justify-start align-center">
              <div className="Feature-list-item-icon">
                <CheckmarkIcon />
              </div>
              <div className="Feature-list-item-text">
                {translate('Automated monthly expense reports')}
              </div>
            </div>
            <div className="Features-list-item flex-row gap-1 justify-start align-center">
              <div className="Feature-list-item-icon">
                <CheckmarkIcon />
              </div>
              <div className="Feature-list-item-text">
                {translate('QuickBooks Online integration')}
              </div>
            </div>
          </div>
          {/* TODO: Fix link */}
          <a
            target="_blank"
            className="LinkText"
            href="https://www.paywithextend.com/pricing"
            rel="noopener noreferrer"
          >
            {translate('View all Pro Features')}
          </a>
          <p className="font-support-medium asterisk-text">
            {translate(
              '*Virtual card functionality for applicable bank partners',
            )}
          </p>
        </RoundCard>
        {overUserLimit && (
          <AlertPanel variant="severe" icon={<AlertTriangleIcon />}>
            <Markdown options={{ disableParsingRawHTML: true }}>
              {translate(
                'Your organization is now on the Starter plan, which includes **up to 5 active users and 10 guests**. To continue using Extend, please deactivate excess users or resubscribe.',
              )}
            </Markdown>
          </AlertPanel>
        )}
        <div className="flex-row justify-between">
          <Button
            variant="secondary"
            onClick={() => {
              if (overUserLimit) {
                history.push(peopleMembersUrl())
              }
              onClose()
            }}
          >
            {translate('Continue with Starter')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              history.push(upgradeSubscriptionUrl())
            }}
          >
            {translate('Subscribe to Pro')}
          </Button>
        </div>
      </div>
    </BaseModal>
  )
}
